import { useState } from "react";
import { useData } from "../context/DataContext";
import { Plus, Trash2, CheckCircle } from "lucide-react";
import { cn } from "../utils/cn";
import { InlineSelect } from "./InlineSelect";
import { Modal, Button, Input, Select, EmptyState } from "./ui";
import { useDeleteConfirmation, useFormReset } from "../utils/hooks";
import { ZUTEILUNG_TYPES } from "../utils/constants";

const Zuteilung = () => {
  const { data, addZuteilung, deleteZuteilung, updateZuteilung } = useData();

  const [showModal, setShowModal] = useState(false);
  const { formData, setFormData, resetForm } = useFormReset({
    lpId: "",
    klasseId: "",
    fachId: "",
    typ: "Standard" as "Standard" | "HK" | "Praktikum" | "SHT" | "Pool",
    lektionen: "2",
    hatHk: false,
    bemerkungen: "",
  });

  const confirmDelete = useDeleteConfirmation("Zuteilung", deleteZuteilung);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.klasseId || !formData.fachId) {
      alert("Bitte Klasse und Fach auswählen!");
      return;
    }
    addZuteilung({
      lpId: formData.lpId || null,
      klasseId: formData.klasseId,
      fachId: formData.fachId,
      typ: formData.typ,
      lektionen: parseFloat(formData.lektionen),
      hatHk: formData.hatHk,
      bemerkungen: formData.bemerkungen,
    });
    resetForm();
    setShowModal(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-3">
        <Button
          onClick={() => setShowModal(true)}
          variant="primary"
          icon={<Plus className="w-4 h-4" />}
        >
          Neue Zuteilung
        </Button>
      </div>

      {data.zuteilungen.length === 0 ? (
        <EmptyState
          icon="📋"
          title="Keine Zuteilungen vorhanden."
        />
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Lehrperson
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Klasse
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fach
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Typ
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Lektionen
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    HK
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Schüler
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Faktor
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Entsch. Std
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Bemerkungen
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Aktionen
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.zuteilungen.map((zt) => {
                  const klasse = data.klassen.find((k) => k.id === zt.klasseId);

                  return (
                    <tr
                      key={zt.id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-4 py-2 whitespace-nowrap">
                        <InlineSelect
                          value={zt.lpId}
                          options={data.lehrpersonen.map((lp) => ({
                            id: lp.id,
                            label: lp.name,
                          }))}
                          onChange={(value) =>
                            updateZuteilung(zt.id, { lpId: value })
                          }
                          placeholder="-"
                          allowClear
                          displayClassName="text-sm font-medium text-gray-900"
                        />
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        <InlineSelect
                          value={zt.klasseId}
                          options={data.klassen.map((kl) => ({
                            id: kl.id,
                            label: `${kl.name} (${kl.bestand} SuS)`,
                          }))}
                          onChange={(value) =>
                            value && updateZuteilung(zt.id, { klasseId: value })
                          }
                          placeholder="?"
                          displayClassName="text-sm text-gray-900"
                        />
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        <InlineSelect
                          value={zt.fachId}
                          options={data.faecher.map((f) => ({
                            id: f.id,
                            label: `${f.kuerzel} - ${f.name}`,
                          }))}
                          onChange={(value) =>
                            value && updateZuteilung(zt.id, { fachId: value })
                          }
                          placeholder="?"
                          displayClassName="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800"
                        />
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        <span
                          className={cn(
                            "inline-flex items-center px-2 py-0.5 rounded text-xs font-medium",
                            zt.typ === "HK"
                              ? "bg-green-100 text-green-800"
                              : "bg-gray-100 text-gray-800"
                          )}
                        >
                          {zt.typ}
                        </span>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                        {zt.lektionen}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-center">
                        {zt.hatHk ? (
                          <CheckCircle className="w-5 h-5 text-green-600 mx-auto" />
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-right text-gray-900">
                        {klasse ? klasse.bestand : "-"}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-right text-gray-500">
                        {zt.entschaedigungFaktor.toFixed(2)}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-right font-semibold text-gray-900">
                        {zt.entschaedigungStunden.toFixed(2)}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-500">
                        {zt.bemerkungen || "-"}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-center">
                        <button
                          onClick={() => confirmDelete(zt.id)}
                          className="inline-flex items-center p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Löschen"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title="Neue Zuteilung"
      >
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <Select
            label="Lehrperson (optional)"
            value={formData.lpId}
            onChange={(e) =>
              setFormData({ ...formData, lpId: e.target.value })
            }
          >
            <option value="">- Noch nicht zugewiesen -</option>
            {data.lehrpersonen.map((lp) => (
              <option key={lp.id} value={lp.id}>
                {lp.name}
              </option>
            ))}
          </Select>
          <Select
            label="Klasse *"
            required
            value={formData.klasseId}
            onChange={(e) =>
              setFormData({ ...formData, klasseId: e.target.value })
            }
          >
            <option value="">- Bitte wählen -</option>
            {data.klassen.map((kl) => (
              <option key={kl.id} value={kl.id}>
                {kl.name} ({kl.bestand} SuS)
              </option>
            ))}
          </Select>
          <Select
            label="Fach *"
            required
            value={formData.fachId}
            onChange={(e) =>
              setFormData({ ...formData, fachId: e.target.value })
            }
          >
            <option value="">- Bitte wählen -</option>
            {data.faecher.map((f) => (
              <option key={f.id} value={f.id}>
                {f.kuerzel} - {f.name}
              </option>
            ))}
          </Select>
          <Select
            label="Typ *"
            value={formData.typ}
            onChange={(e) =>
              setFormData({ ...formData, typ: e.target.value as any })
            }
          >
            {ZUTEILUNG_TYPES.map((type) => (
              <option key={type.id} value={type.id}>
                {type.label}
              </option>
            ))}
          </Select>
          <Input
            label="Anzahl Lektionen *"
            type="number"
            required
            min="0.5"
            step="0.5"
            value={formData.lektionen}
            onChange={(e) =>
              setFormData({ ...formData, lektionen: e.target.value })
            }
          />
          <div className="flex items-center">
            <input
              type="checkbox"
              id="hatHk"
              checked={formData.hatHk}
              onChange={(e) =>
                setFormData({ ...formData, hatHk: e.target.checked })
              }
              className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
            />
            <label
              htmlFor="hatHk"
              className="ml-2 text-sm font-medium text-gray-700"
            >
              Hat Halbklasse (HK)
            </label>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Bemerkungen
            </label>
            <textarea
              value={formData.bemerkungen}
              onChange={(e) =>
                setFormData({ ...formData, bemerkungen: e.target.value })
              }
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <Button type="submit" variant="primary" className="flex-1">
              Speichern
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setShowModal(false)}
              className="flex-1"
            >
              Abbrechen
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Zuteilung;
